import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { trackEvent } from '@/utils/analytics';
import { getVisitorId } from '@/lib/marketing';

export type AuthMode = 'login' | 'register' | 'forgot';

interface AuthFormProps {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onSuccess: (token: string, user: any) => void;
  initialEmail?: string;
  showCancel?: boolean;
  onCancel?: () => void;
  // Compact register form — drops the Confirm Password field. Used on promo
  // landing pages where signup friction matters more than typo protection.
  compact?: boolean;
}

type ErrorType = 'auth_failed' | 'email_exists' | 'validation' | 'unknown' | null;

// Casting owns the passwordless code lifecycle (shared user DB + JWT secret) —
// its login-code routes are CORS-open, so the browser calls them directly.
const CASTING_API = 'https://casting.heyjoe.io/api';

export const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  onModeChange,
  onSuccess,
  initialEmail = '',
  showCancel = false,
  onCancel,
  compact = false,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [resetSent, setResetSent] = useState(false);

  // Passwordless code sign-in (sub-flow of login mode)
  const [codeStep, setCodeStep] = useState<'off' | 'request' | 'enter'>('off');
  const [codeMethod, setCodeMethod] = useState<'email' | 'sms'>('email');
  const [smsHint, setSmsHint] = useState<string | null>(null);
  const [loginCode, setLoginCode] = useState('');

  const resetForm = () => {
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setError(null);
    setErrorType(null);
    setResetSent(false);
    setCodeStep('off');
    setLoginCode('');
    setSmsHint(null);
  };

  const switchMode = (newMode: AuthMode) => {
    resetForm();
    onModeChange(newMode);
  };

  // Google OAuth
  const handleGoogleSuccess = async (googleResponse: any) => {
    setIsLoading(true);
    setError(null);
    setErrorType(null);

    try {
      const userInfoRes = await fetch(
        `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${googleResponse.access_token}`,
        { headers: { Authorization: `Bearer ${googleResponse.access_token}`, Accept: 'application/json' } }
      );
      const googleUser = await userInfoRes.json();

      const loginRes = await fetch('/api/auth/google-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: googleUser.email, token: googleResponse.access_token }),
      });
      const loginData = await loginRes.json();

      if (loginRes.ok && loginData.success && loginData.token) {
        onSuccess(loginData.token, loginData.user);
        return;
      }

      const registerRes = await fetch('/api/auth/google-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: googleUser.email,
          token: googleResponse.access_token,
          first_name: googleUser.given_name || '',
          last_name: googleUser.family_name || '',
        }),
      });
      const registerData = await registerRes.json();

      if (registerRes.ok && registerData.success && registerData.token) {
        trackEvent('sign_up', { method: 'google' });
        onSuccess(registerData.token, registerData.user);
      } else {
        setError(registerData.error || 'Google sign-in failed');
        setErrorType('unknown');
      }
    } catch {
      setError('Google sign-in failed. Please try again.');
      setErrorType('unknown');
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      setError('Google sign-in was cancelled or failed.');
      setErrorType('unknown');
    },
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setErrorType(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.token && data.user) {
        onSuccess(data.token, data.user);
      } else {
        setError(data.error || 'Invalid email or password');
        setErrorType(data.errorType || 'unknown');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
      setErrorType('unknown');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorType(null);

    if (!compact && password !== confirmPassword) {
      setError('Passwords do not match');
      setErrorType('validation');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setErrorType('validation');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // visitorId binds this account to the anonymous ad click that brought them
        // here, so a purchase later in the Stripe webhook can name the campaign.
        body: JSON.stringify({ email, password, first_name: firstName, last_name: lastName, visitorId: getVisitorId() }),
      });

      const data = await response.json();

      if (response.ok && data.token && data.user) {
        trackEvent('sign_up', { method: 'email' });
        onSuccess(data.token, data.user);
      } else if (data.errorType === 'email_exists') {
        setError(null);
        setErrorType('email_exists');
      } else {
        setError(data.error || 'Registration failed');
        setErrorType(data.errorType || 'unknown');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
      setErrorType('unknown');
    } finally {
      setIsLoading(false);
    }
  };

  const sendLoginCode = async (method: 'email' | 'sms') => {
    setError(null);
    setErrorType(null);
    if (!email) {
      setError('Enter your email first');
      setErrorType('validation');
      return;
    }
    setIsLoading(true);
    try {
      if (method === 'sms') {
        // Only text accounts that actually have a phone number on file.
        const chk = await fetch(`${CASTING_API}/auth/check-user/${encodeURIComponent(email.toLowerCase())}`).then((r) => r.json());
        if (!chk.exists) {
          setError('No account found with that email address.');
          setErrorType('auth_failed');
          return;
        }
        if (!chk.sms_available) {
          setError("This account doesn't have a phone number on file — use the email code instead.");
          setErrorType('validation');
          return;
        }
      }
      const res = await fetch(`${CASTING_API}/auth/login-code/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase(), method }),
      });
      if (res.status === 429) {
        setError('Too many code requests — wait a minute and try again.');
        setErrorType('validation');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setCodeMethod(method);
        setSmsHint(data.sms_hint || null);
        setLoginCode('');
        setCodeStep('enter');
      } else if (data.error === 'sms_unavailable') {
        setError("This account doesn't have a phone number on file — use the email code instead.");
        setErrorType('validation');
      } else {
        setError(data.message || data.error || 'Could not send the code.');
        setErrorType('unknown');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
      setErrorType('unknown');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = loginCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code');
      setErrorType('validation');
      return;
    }
    setIsLoading(true);
    setError(null);
    setErrorType(null);
    try {
      const res = await fetch(`${CASTING_API}/auth/login-code/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase(), code }),
      });
      const data = await res.json();
      if (data.success && data.token) {
        // verify returns the token only — fetch the user so onSuccess gets both.
        let userData: any = { email };
        try {
          const meRes = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${data.token}` } });
          if (meRes.ok) {
            const me = await meRes.json();
            userData = me.user || me;
          }
        } catch {
          // email-only fallback; AuthContext's validate poll fills in the rest
        }
        onSuccess(data.token, userData);
      } else if (data.requiresVerification) {
        setError('Please verify your email first — check your inbox for the verification link.');
        setErrorType('auth_failed');
      } else {
        setError(typeof data.error === 'string' ? data.error : 'Invalid or expired code.');
        setErrorType('auth_failed');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
      setErrorType('unknown');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/reset-password-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (data.success) {
        setResetSent(true);
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password view
  if (mode === 'forgot') {
    if (resetSent) {
      return (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
          <div>
            <p className="font-medium">Check your email</p>
            <p className="text-sm text-muted-foreground mt-1">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => switchMode('login')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to sign in
          </Button>
        </div>
      );
    }

    return (
      <form onSubmit={handleForgotPassword} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <p className="text-sm text-muted-foreground">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            autoFocus
          />
        </div>

        <Button type="submit" disabled={isLoading || !email} className="w-full">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            'Send Reset Link'
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className="underline hover:text-foreground"
          >
            Back to sign in
          </button>
        </p>
      </form>
    );
  }

  return (
    <div className="space-y-4">
      {/* Google Sign-In Button */}
      <button
        type="button"
        className="inline-flex w-full items-center justify-center gap-2 transition-opacity disabled:cursor-not-allowed"
        onClick={() => googleLogin()}
        disabled={isLoading}
        style={{
          padding: '10px 16px',
          borderRadius: 999,
          background: '#fff',
          border: '1px solid var(--hj-beige-line)',
          color: 'oklch(0.18 0.005 70)',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.6 : 1,
          minHeight: 44,
        }}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
        )}
        Continue with Google
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full" style={{ borderTop: '1px solid var(--hj-hairline)' }} />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span
            className="mono-label"
            style={{
              background: 'var(--hj-surface-1)',
              color: 'var(--hj-ink-3)',
              padding: '0 10px',
            }}
          >
            or
          </span>
        </div>
      </div>

      {/* Passwordless code sign-in — replaces the password form while active */}
      {mode === 'login' && codeStep !== 'off' ? (
        <form onSubmit={codeStep === 'enter' ? handleVerifyCode : (e) => { e.preventDefault(); sendLoginCode('email'); }} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {codeStep === 'request' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="code-email">Email</Label>
                <Input
                  id="code-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div className="flex flex-col gap-2">
                <Button type="submit" disabled={isLoading || !email} className="w-full">
                  {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Email me a code
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading || !email}
                  className="w-full"
                  onClick={() => sendLoginCode('sms')}
                >
                  Text me a code
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {codeMethod === 'email' ? (
                  <>We emailed a 6-digit code to <strong>{email}</strong>.</>
                ) : (
                  <>We texted a 6-digit code to the phone number {smsHint ? <>ending in <strong>{smsHint}</strong></> : 'on your account'}.</>
                )}
              </p>

              <div className="space-y-2">
                <Label htmlFor="login-code">6-digit code</Label>
                <Input
                  id="login-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, ''))}
                  disabled={isLoading}
                  autoFocus
                  className="tracking-widest text-center"
                />
              </div>

              <Button type="submit" disabled={isLoading || loginCode.trim().length !== 6} className="w-full">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                <button
                  type="button"
                  onClick={() => sendLoginCode(codeMethod)}
                  disabled={isLoading}
                  className="underline hover:text-foreground"
                >
                  Resend code
                </button>
                {' · '}
                <button
                  type="button"
                  onClick={() => sendLoginCode(codeMethod === 'email' ? 'sms' : 'email')}
                  disabled={isLoading}
                  className="underline hover:text-foreground"
                >
                  {codeMethod === 'email' ? 'Text it instead' : 'Email it instead'}
                </button>
              </p>
            </>
          )}

          <p className="text-xs text-center text-muted-foreground">
            <button
              type="button"
              onClick={() => { setCodeStep('off'); setLoginCode(''); setError(null); setErrorType(null); }}
              className="underline hover:text-foreground"
            >
              Back to password sign-in
            </button>
          </p>
        </form>
      ) : (
      <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {errorType === 'email_exists' && !error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              An account with this email already exists.{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="underline font-medium hover:text-destructive-foreground/80"
              >
                Sign in instead
              </button>
            </AlertDescription>
          </Alert>
        )}

        {mode === 'register' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            {mode === 'login' && (
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Forgot password?
              </button>
            )}
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            minLength={mode === 'register' ? 6 : undefined}
          />
          {mode === 'register' && (
            <p className="text-xs text-muted-foreground">6+ characters</p>
          )}
        </div>

        {mode === 'register' && !compact && (
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={
              isLoading ||
              !email ||
              !password ||
              (mode === 'register' && (!firstName || !lastName || (!compact && !confirmPassword)))
            }
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mode === 'login' ? 'Signing in...' : 'Creating account...'}
              </>
            ) : mode === 'login' ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </Button>
          {showCancel && onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
          )}
        </div>

        {mode === 'login' && (
          <p className="text-xs text-center text-muted-foreground">
            <button
              type="button"
              onClick={() => { setError(null); setErrorType(null); setCodeStep('request'); }}
              className="underline hover:text-foreground"
            >
              Email or text me a sign-in code instead
            </button>
          </p>
        )}

        <p className="text-xs text-center text-muted-foreground mt-4">
          {mode === 'login' ? (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('register')}
                className="underline hover:text-foreground"
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="underline hover:text-foreground"
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </form>
      )}
    </div>
  );
};
